import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Popover,
  Stack,
  Typography
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { usePredictions } from '../../context';
import {
  CATEGORY_LABELS,
  PREDICTION_CATEGORIES,
  PredictionCategory
} from '../../constants/categories';

export const CategoryFilter = () => {
  const { excludedCategories, setExcludedCategories, categoryCounts } =
    usePredictions();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [staged, setStaged] = useState<string[]>(excludedCategories);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (open) setStaged(excludedCategories);
  }, [open, excludedCategories]);

  const toggle = (category: PredictionCategory) => {
    setStaged((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const handleApply = () => {
    setExcludedCategories(staged);
    setAnchorEl(null);
  };

  const handleClear = () => setStaged([]);

  const hiddenCount = excludedCategories.length;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<FilterAltIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          color: 'common.white',
          borderColor: hiddenCount > 0 ? 'primary.main' : 'rgba(255,255,255,0.3)',
          textTransform: 'none',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'rgba(255,255,255,0.05)'
          }
        }}
      >
        Categories{hiddenCount > 0 ? ` · ${hiddenCount} hidden` : ''}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 260 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Hide categories
          </Typography>
          <Stack>
            {PREDICTION_CATEGORIES.map((category) => {
              const count = categoryCounts[category] ?? 0;
              return (
                <Stack
                  key={category}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ pr: 1 }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={staged.includes(category)}
                        onChange={() => toggle(category)}
                      />
                    }
                    label={CATEGORY_LABELS[category]}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {count}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              size="small"
              onClick={handleClear}
              disabled={staged.length === 0}
            >
              Clear
            </Button>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setAnchorEl(null)}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={handleApply}>
                Apply
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};
